import { api } from "@Aspecto/backend/convex/_generated/api";
import { useClerk } from "@clerk/tanstack-react-start";
import { useConvex, useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Download, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { exportAsCsvZip, exportAsJson } from "@/lib/export-utils";
import { invalidateClientClerkAuthCache } from "@/routes/__root";
import { useCurrentCompany } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/admin/data-privacy")({
  component: DataPrivacySettings,
});

const EXPORT_CATEGORIES = [
  { key: "members", label: "Members" },
  { key: "groups", label: "Groups" },
  { key: "groupMembers", label: "Group Members" },
  { key: "posts", label: "Posts" },
  { key: "comments", label: "Comments" },
  { key: "events", label: "Events" },
  { key: "eventAttendees", label: "Event RSVPs" },
  { key: "eventAttendance", label: "Event Attendance" },
  { key: "newsletters", label: "Digests" },
] as const;

type CategoryKey = (typeof EXPORT_CATEGORIES)[number]["key"];

const DEFAULT_CATEGORIES = Object.fromEntries(
  EXPORT_CATEGORIES.map(({ key }) => [key, true]),
) as Record<CategoryKey, boolean>;

function DataPrivacySettings() {
  const { signOut } = useClerk();
  const { data: company } = useCurrentCompany();
  const convex = useConvex();
  const deleteCompany = useMutation(api.companies.deleteCompany);

  const [selectedCategories, setSelectedCategories] = useState<Record<CategoryKey, boolean>>({
    ...DEFAULT_CATEGORIES,
  });
  const [exportFormat, setExportFormat] = useState<"json" | "csv">("json");
  const [isExporting, setIsExporting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const allSelected = EXPORT_CATEGORIES.every(({ key }) => selectedCategories[key]);
  const anySelected = EXPORT_CATEGORIES.some(({ key }) => selectedCategories[key]);

  const handleExport = async () => {
    if (!anySelected) {
      toast.error("Please select at least one data category");
      return;
    }

    setIsExporting(true);
    try {
      const result = await convex.query(api.dataExport.exportOrganisationData, {
        categories: selectedCategories,
      });

      if (exportFormat === "json") {
        exportAsJson(result, result.companyName);
      } else {
        await exportAsCsvZip(
          result.data as Record<string, Record<string, unknown>[]>,
          result.companyName,
        );
      }

      toast.success("Data exported successfully!");
    } catch {
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <SectionCard title="Export Data" description="Download a copy of your organization's data">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Data categories</Label>
            <Button
              variant="link"
              size="xs"
              type="button"
              onClick={() => {
                const newValue = !allSelected;
                setSelectedCategories(
                  Object.fromEntries(EXPORT_CATEGORIES.map(({ key }) => [key, newValue])) as Record<
                    CategoryKey,
                    boolean
                  >,
                );
              }}
            >
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {EXPORT_CATEGORIES.map(({ key, label }) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={selectedCategories[key]}
                  onCheckedChange={(checked) =>
                    setSelectedCategories((prev) => ({
                      ...prev,
                      [key]: !!checked,
                    }))
                  }
                />
                {label}
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Export format:</span>
            <div className="flex gap-1">
              <Button
                size="sm"
                type="button"
                variant={exportFormat === "json" ? "default" : "outline"}
                onClick={() => setExportFormat("json")}
              >
                JSON
              </Button>
              <Button
                size="sm"
                type="button"
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
              >
                CSV (ZIP)
              </Button>
            </div>
          </div>

          <Button type="button" onClick={handleExport} disabled={isExporting || !anySelected}>
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download Export
              </>
            )}
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        title="Danger Zone"
        description="Irreversible actions for your organization"
        className="mt-6"
      >
        <div className="flex items-center justify-between rounded-lg border border-destructive/50 p-4">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">Delete this organization</p>
            <p className="text-sm text-muted-foreground">
              Permanently delete this organization and all its data
            </p>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </SectionCard>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Organization
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{company?.name}"? This action cannot be undone. All
              groups, posts, events, members, and other data will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await deleteCompany();
                  invalidateClientClerkAuthCache();
                  await signOut({ redirectUrl: "/sign-in" });
                } catch {
                  toast.error("Failed to delete organization. Please try again.");
                  setIsDeleting(false);
                }
              }}
            >
              {isDeleting ? "Deleting..." : "Delete Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
